import { Page } from "./base";
import homeTemplate from "../templates/pages/home.html?raw";
import NavBar from "../sections/navbar";
import Footer from "../sections/footer";
import { getUserProfile, UserProfile } from "../services/profile";

export default class HomePage extends Page {
  constructor() {
    super(homeTemplate);
    this.loadPage();
  }

  public async loadPage(): Promise<void> {
    super.init();
    new NavBar();
    await this.loadWineProfile();
    new Footer();
  }

  private async loadWineProfile(): Promise<void> {
    try {
      const profile = await getUserProfile();
      if (profile) {
        this.renderWineProfile(profile);
      } else {
        this.renderError();
      }
    } catch (error) {
      console.error("Failed to load wine profile:", error);
      this.renderError();
    }
  }

  private renderWineProfile(profile: UserProfile): void {
    const container = document.querySelector('.container.mx-auto.px-8');
    if (!container) return;

    const wineProfileSection = this.createWineProfileSection(profile);
    container.innerHTML = wineProfileSection;
    
    // Add event listener for reset preferences button
    this.setupResetPreferencesButton();
  }

  private createWineProfileSection(profile: UserProfile): string {
    const wineProfileType = profile.wine_profile_type || 'Not Set';
    const experienceLevel = this.formatExperienceLevel(profile.experience_level);
    const drinkingFrequency = this.formatDrinkingFrequency(profile.drinking_frequency);
    
    return `
      <div class="max-w-4xl mx-auto py-8">
        <!-- Header -->
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-800 mb-2">Welcome back, ${profile.first_name}!</h1>
          <p class="text-gray-600">Your personalized wine profile</p>
          <button id="resetPreferencesBtn" class="mt-4 bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors">
            Reset my preferences
          </button>
        </div>

        <!-- Wine Profile Type Card -->
        <div class="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div class="text-center">
            <h2 class="text-2xl font-semibold text-purple-600 mb-3">${wineProfileType}</h2>
            <p class="text-gray-600 text-lg leading-relaxed">${this.getProfileDescription(wineProfileType)}</p>
          </div>
        </div>

        <!-- Profile Details Grid -->
        <div class="grid md:grid-cols-2 gap-6 mb-6">
          <!-- Experience & Habits -->
          <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">Experience & Habits</h3>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-gray-600">Experience Level:</span>
                <span class="font-medium">${experienceLevel}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-600">Drinking Frequency:</span>
                <span class="font-medium">${drinkingFrequency}</span>
              </div>
            </div>
          </div>

          <!-- Wine Preferences -->
          <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">Wine Preferences</h3>
            ${this.renderWineTypePreferences(profile.wine_type_preferences)}
          </div>
        </div>

        <!-- Occasions & Regions -->
        <div class="grid md:grid-cols-2 gap-6 mb-6">
          <!-- Typical Occasions -->
          <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">Typical Occasions</h3>
            ${this.renderOccasions(profile.typical_occasions)}
          </div>

          <!-- Preferred Regions -->
          <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">Preferred Regions</h3>
            ${this.renderRegions(profile.preferred_regions)}
          </div>
        </div>

        <!-- Flavors -->
        <div class="grid md:grid-cols-2 gap-6">
          <!-- Loved Flavors -->
          <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">Loved Flavors</h3>
            ${this.renderFlavors(profile.loved_flavors, 'loved')}
          </div>

          <!-- Disliked Flavors -->
          <div class="bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-800 mb-4">Disliked Flavors</h3>
            ${this.renderFlavors(profile.disliked_flavors, 'disliked')}
          </div>
        </div>
      </div>
    `;
  }

  private getProfileDescription(profileType: string): string {
    const descriptions: Record<string, string> = {
      "The Curious Explorer": "You're beginning your wine journey with enthusiasm and openness to discovering new flavors at accessible prices.",
      "The Enthusiastic Connoisseur": "You have developing wine knowledge and actively seek to explore more complex flavors and styles.",
      "The Wine Aficionado": "You have extensive wine knowledge and appreciate the nuances and complexity of exceptional bottles.",
      "The Balanced Enthusiast": "You enjoy wine as part of a well-rounded lifestyle, balancing quality with value and versatility.",
      "The Social Sipper": "You enjoy wine primarily in social settings and special occasions, preferring approachable and crowd-pleasing styles.",
      "The Sophisticated Collector": "You have refined tastes and deep appreciation for rare, premium, and collectible wines with investment potential."
    };
    
    return descriptions[profileType] || "Your unique wine profile reflects your personal preferences and journey.";
  }

  private formatExperienceLevel(level?: string): string {
    const levels: Record<string, string> = {
      'beginner': 'Beginner',
      'intermediate': 'Intermediate', 
      'advanced': 'Advanced',
      'expert': 'Expert'
    };
    return levels[level || ''] || 'Not specified';
  }

  private formatDrinkingFrequency(frequency?: string): string {
    const frequencies: Record<string, string> = {
      'rarely': 'Rarely',
      'monthly': 'Monthly',
      'weekly': 'Weekly',
      'several_times_week': 'Several times a week',
      'daily': 'Daily'
    };
    return frequencies[frequency || ''] || 'Not specified';
  }

  private renderWineTypePreferences(preferences?: Record<string, number>): string {
    if (!preferences || Object.keys(preferences).length === 0) {
      return '<p class="text-gray-500">No wine type preferences set</p>';
    }

    return Object.entries(preferences)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 5)
      .map(([type, level]) => `
        <div class="flex justify-between items-center mb-2">
          <span class="text-gray-600 capitalize">${type.replace('_', ' ')}</span>
          <div class="flex items-center">
            <div class="w-20 bg-gray-200 rounded-full h-2 mr-2">
              <div class="bg-purple-500 h-2 rounded-full" style="width: ${(level/10)*100}%"></div>
            </div>
            <span class="text-sm font-medium w-6">${level}</span>
          </div>
        </div>
      `).join('');
  }

  private renderOccasions(occasions?: string[]): string {
    if (!occasions || occasions.length === 0) {
      return '<p class="text-gray-500">No occasions specified</p>';
    }

    return occasions.map(occasion => `
      <span class="inline-block bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full mr-2 mb-2">
        ${occasion.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
      </span>
    `).join('');
  }

  private renderRegions(regions?: string[]): string {
    if (!regions || regions.length === 0) {
      return '<p class="text-gray-500">No preferred regions specified</p>';
    }

    return regions.slice(0, 8).map(region => `
      <span class="inline-block bg-green-100 text-green-800 text-sm px-3 py-1 rounded-full mr-2 mb-2">
        ${region}
      </span>
    `).join('');
  }

  private renderFlavors(flavors?: Record<string, any>, type?: string): string {
    if (!flavors || Object.keys(flavors).length === 0) {
      return `<p class="text-gray-500">No ${type} flavors specified</p>`;
    }

    const colorClass = type === 'loved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
    
    return Object.entries(flavors).map(([category, flavorList]) => {
      if (!Array.isArray(flavorList)) return '';
      
      return flavorList.slice(0, 3).map((flavor: any) => `
        <span class="inline-block ${colorClass} text-sm px-3 py-1 rounded-full mr-2 mb-2">
          ${flavor.descriptor} (${category})
        </span>
      `).join('');
    }).join('');
  }

  private setupResetPreferencesButton(): void {
    const resetBtn = document.getElementById('resetPreferencesBtn');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        // Set a flag to bypass profile completion check
        sessionStorage.setItem('bypassProfileCheck', 'true');
        // Navigate to profiler
        (window as any).appRouter?.navigate('/profiler');
      });
    }
  }

  private renderError(): void {
    const container = document.querySelector('.container.mx-auto.px-8');
    if (!container) return;

    container.innerHTML = `
      <div class="text-center py-12">
        <h2 class="text-2xl font-semibold text-gray-800 mb-4">Unable to load wine profile</h2>
        <p class="text-gray-600 mb-6">We couldn't retrieve your wine profile data. Please try refreshing the page.</p>
        <button onclick="window.location.reload()" class="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors">
          Refresh Page
        </button>
      </div>
    `;
  }
}
